// In-memory items and units for demo mode (no Supabase configured).
// Mirrors the database behaviour that matters to the UI: keyset paging on
// code, type/active filters, search on "code description", immutable code.

import type { ItemDetail, ItemInput, ItemListParams, ItemPage, ItemRow, Uom } from "./api";

const PAGE = 50;
const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));
let seq = 0;
const nextId = (prefix: string) => `${prefix}-${++seq}`;

const uoms: Uom[] = [
  ["PCS", "Sztuki", "count", 0],
  ["KG", "Kilogram", "mass", 3],
  ["G", "Gram", "mass", 0],
  ["T", "Tona", "mass", 3],
  ["M", "Metr", "length", 3],
  ["MM", "Milimetr", "length", 0],
  ["M2", "Metr kwadratowy", "area", 3],
  ["L", "Litr", "volume", 3],
  ["ML", "Mililitr", "volume", 0],
  ["H", "Godzina", "time", 2],
  ["BOX", "Karton", "count", 0],
  ["PAL", "Paleta", "count", 0],
].map(([code, name, dimension, decimals]) => ({
  id: `uom-${code}`,
  code: code as string,
  name: name as string,
  dimension: dimension as Uom["dimension"],
  decimals: decimals as number,
  is_active: true,
}));

const uomById = (id: string) => uoms.find((u) => u.id === id) ?? null;

interface Stored extends ItemInput {
  id: string;
  updated_at: string;
  conversions: { id: string; from: string; to: string; factor: string }[];
}

const items: Stored[] = (
  [
    ["FR-2210", "Seat frame, steel", "material", "PCS", "purchase", false, false],
    ["FR-2215", "Seat frame, aluminium", "material", "PCS", "purchase", false, false],
    ["FOAM-45", "Seat foam 45 kg/m³", "material", "PCS", "purchase", true, false],
    ["FOAM-60", "Seat foam 60 kg/m³", "material", "PCS", "purchase", true, false],
    ["FAB-GR3", "Seat fabric, grey", "material", "M", "purchase", true, false],
    ["FAB-BK1", "Seat fabric, black", "material", "M", "purchase", true, false],
    ["LTH-BR2", "Leather, brown", "material", "M2", "purchase", true, false],
    ["SC-M6", "Screw M6×20", "material", "PCS", "purchase", false, false],
    ["SC-M8", "Screw M8×25", "material", "PCS", "purchase", false, false],
    ["NUT-M6", "Nut M6", "material", "PCS", "purchase", false, false],
    ["GLUE-PU2", "PU adhesive", "material", "KG", "purchase", true, false],
    ["CLN-IPA", "Isopropanol", "material", "L", "purchase", true, false],
    ["PRM-07", "Primer", "material", "L", "purchase", true, false],
    ["BOX-600", "Carton box 600×400", "material", "PCS", "purchase", false, false],
    ["LBL-A6", "Shipping label A6", "material", "PCS", "purchase", false, false],
    ["CUS-600", "Seat cushion assembly", "semi_finished", "PCS", "manufacture", false, false],
    ["BCK-600", "Backrest assembly", "semi_finished", "PCS", "manufacture", false, false],
    ["ST-600", "Seat assembly, grey fabric", "finished_good", "PCS", "manufacture", false, true],
    ["ST-610", "Seat assembly, black fabric", "finished_good", "PCS", "manufacture", false, true],
    ["ST-700L", "Premium seat, leather", "finished_good", "PCS", "manufacture", false, true],
    ["SRV-CAL", "Calibration service", "service", "H", "purchase", false, false],
  ] as const
).map(([code, description, item_type, uom, procurement_type, lot, serial]) => ({
  id: `item-${code}`,
  code,
  description,
  item_type,
  base_uom_id: `uom-${uom}`,
  procurement_type,
  lot_controlled: lot,
  serial_controlled: serial,
  is_active: true,
  updated_at: new Date().toISOString(),
  conversions: code.startsWith("SC-") || code === "NUT-M6" ? [{ id: nextId("conv"), from: "uom-BOX", to: "uom-PCS", factor: "500" }] : [],
}));

function toRow(i: Stored): ItemRow {
  return {
    id: i.id,
    code: i.code,
    description: i.description,
    item_type: i.item_type,
    procurement_type: i.procurement_type,
    lot_controlled: i.lot_controlled,
    serial_controlled: i.serial_controlled,
    is_active: i.is_active,
    base_uom: { code: uomById(i.base_uom_id)?.code ?? "?" },
  };
}

function toDetail(i: Stored): ItemDetail {
  const u = uomById(i.base_uom_id);
  return {
    ...toRow(i),
    base_uom_id: i.base_uom_id,
    base_uom: u ? { id: u.id, code: u.code, name: u.name, decimals: u.decimals } : null,
    updated_at: i.updated_at,
    conversions: i.conversions.map((c) => ({
      id: c.id,
      factor: c.factor,
      from_uom: { id: c.from, code: uomById(c.from)?.code ?? "?" },
      to_uom: { id: c.to, code: uomById(c.to)?.code ?? "?" },
    })),
  };
}

export const demoApi = {
  async listItems(p: ItemListParams, cursor: string | null): Promise<ItemPage> {
    await delay(80);
    const q = p.q.trim().toLocaleLowerCase();
    const filtered = items
      .filter((i) => p.inactive || i.is_active)
      .filter((i) => p.type === "all" || i.item_type === p.type)
      .filter((i) => !q || `${i.code} ${i.description}`.toLocaleLowerCase().includes(q))
      .sort((a, b) => (a.code < b.code ? -1 : 1))
      .filter((i) => !cursor || i.code > cursor);
    const page = filtered.slice(0, PAGE);
    return { rows: page.map(toRow), nextCursor: filtered.length > PAGE ? page[page.length - 1]!.code : null };
  },
  async getItem(id: string): Promise<ItemDetail | null> {
    await delay(50);
    const i = items.find((x) => x.id === id);
    return i ? toDetail(i) : null;
  },
  async listUoms(): Promise<Uom[]> {
    await delay(30);
    return uoms;
  },
  async createItem(input: ItemInput): Promise<string> {
    await delay(120);
    if (items.some((i) => i.code === input.code)) {
      throw Object.assign(new Error("duplicate key"), { code: "23505" });
    }
    const id = `item-${input.code}`;
    items.push({ ...input, id, updated_at: new Date().toISOString(), conversions: [] });
    return id;
  },
  async updateItem(id: string, input: Omit<ItemInput, "code">): Promise<void> {
    await delay(120);
    const i = items.find((x) => x.id === id);
    if (i) Object.assign(i, input, { updated_at: new Date().toISOString() });
  },
  async addConversion(itemId: string, from: string, to: string, factor: string): Promise<void> {
    await delay(80);
    items.find((x) => x.id === itemId)?.conversions.push({ id: nextId("conv"), from, to, factor });
  },
  async deleteConversion(id: string): Promise<void> {
    await delay(80);
    for (const i of items) i.conversions = i.conversions.filter((c) => c.id !== id);
  },
};
